import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API, useAuth, authFetch } from "@/App";
import AlbumCard from "@/components/AlbumCard";
import StarRating from "@/components/StarRating";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Disc3, Users, LayoutList, Star, UserPlus, UserMinus, Calendar, Heart } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export default function Profile() {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [logs, setLogs] = useState([]);
  const [lists, setLists] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  const isOwnProfile = currentUser?.user_id === userId;

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, logsRes, listsRes, favoritesRes] = await Promise.all([
        authFetch(`${API}/users/${userId}`),
        authFetch(`${API}/users/${userId}/logs`),
        authFetch(`${API}/users/${userId}/lists`),
        authFetch(`${API}/users/${userId}/favorites`),
      ]);

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setProfile(profileData);
        setIsFollowing(profileData.is_following);
        setFollowerCount(profileData.stats?.followers || 0);
      }

      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData);
      }

      if (listsRes.ok) {
        const listsData = await listsRes.json();
        setLists(listsData);
      }

      if (favoritesRes.ok) {
        const favoritesData = await favoritesRes.json();
        setFavorites(favoritesData);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      const response = await authFetch(`${API}/users/${userId}/follow`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        setIsFollowing(data.is_following);
        setFollowerCount(data.follower_count);
        toast.success(data.is_following ? "Following!" : "Unfollowed");
      }
    } catch (error) {
      toast.error("Failed to follow");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-36">
        <div className="max-w-4xl mx-auto px-4 animate-pulse">
          <div className="h-48 bg-muted rounded-xl mb-8" />
          <div className="flex gap-4 mb-8">
            <div className="w-32 h-32 rounded-full bg-muted" />
            <div className="flex-1">
              <div className="w-48 h-8 bg-muted rounded mb-2" />
              <div className="w-32 h-4 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <div className="text-center">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">User not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0510] pt-16" data-testid="profile-page">
      {/* Cover gradient */}
      <div className="h-48 md:h-64 bg-gradient-to-br from-purple-800/40 via-purple-900/30 to-[#0A0510]" />

      {/* Profile info */}
      <div className="max-w-4xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 mb-8">
          <Avatar className="w-32 h-32 border-4 border-[#0A0510] shadow-xl">
            <AvatarImage src={profile.picture} alt={profile.name} />
            <AvatarFallback className="bg-purple-500/20 text-purple-400 text-4xl">
              {profile.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-3xl md:text-4xl font-bold mb-1 text-white">{profile.name}</h1>
            {profile.bio && (
              <p className="text-white/60 mb-3">{profile.bio}</p>
            )}
            <div className="flex items-center gap-2 text-sm text-white/50 justify-center sm:justify-start">
              <Calendar className="w-4 h-4" />
              Joined{" "}
              {profile.created_at &&
                formatDistanceToNow(new Date(profile.created_at), { addSuffix: true })}
            </div>
          </div>

          {!isOwnProfile && (
            <Button
              onClick={handleFollow}
              variant={isFollowing ? "outline" : "default"}
              className="rounded-full px-6"
              data-testid="follow-btn"
            >
              {isFollowing ? (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Unfollow
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Follow
                </>
              )}
            </Button>
          )}
        </div>

        {/* Favorite Genres - check both top level and nested for compatibility */}
        {(profile.profile?.favorite_genres?.length > 0 || profile.favorite_genres?.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-6">
            {(profile.profile?.favorite_genres || profile.favorite_genres).map((genre) => (
              <Badge 
                key={genre} 
                className="bg-purple-500/20 text-purple-300 border-purple-500/30"
              >
                {genre}
              </Badge>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8 glass-card rounded-xl p-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-400">{profile.stats?.albums_logged || 0}</p>
            <p className="text-xs text-white/50">Albums</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{profile.stats?.reviews || 0}</p>
            <p className="text-xs text-white/50">Reviews</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{followerCount}</p>
            <p className="text-xs text-white/50">Followers</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{profile.stats?.following || 0}</p>
            <p className="text-xs text-white/50">Following</p>
          </div>
        </div>

        {/* Favorite Albums Section */}
        {favorites.length > 0 && (
          <div className="mb-8" data-testid="favorites-section">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
              <h2 className="text-lg font-semibold text-white">Favorite Albums</h2>
            </div>
            <div className="grid grid-cols-5 gap-4">
              {favorites.map((fav) => {
                const album = fav.album;
                if (!album) return null;
                const cover = album.cover_xl || album.cover_big || album.cover_medium || album.cover_url || album.cover_small || "";
                const albumLink = album.album_id?.startsWith("deezer_") || album.album_id?.startsWith("discogs_")
                  ? `/album/${album.album_id}`
                  : album.deezer_id
                    ? `/album/deezer_${album.deezer_id}`
                    : `/album/${album.album_id}`;
                
                return (
                  <Link
                    key={fav.favorite_id}
                    to={albumLink}
                    className="group block"
                    data-testid={`favorite-album-${fav.album_id}`}
                  >
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-white/5 ring-2 ring-pink-500/30 group-hover:ring-pink-500 transition-all">
                      <img
                        src={cover}
                        alt={album.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => { e.target.src = "https://placehold.co/150x150/1a1a2e/9333ea?text=?"; }}
                      />
                    </div>
                    <p className="mt-2 text-sm font-medium text-white truncate group-hover:text-pink-400 transition-colors">
                      {album.title}
                    </p>
                    <p className="text-xs text-white/50 truncate">{album.artist}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="activity" className="w-full">
          <TabsList className="bg-purple-950/50 p-1 rounded-full mb-8">
            <TabsTrigger
              value="activity"
              className="rounded-full text-white/70 data-[state=active]:bg-purple-500 data-[state=active]:text-white"
            >
              Activity
            </TabsTrigger>
            <TabsTrigger
              value="lists"
              className="rounded-full text-white/70 data-[state=active]:bg-purple-500 data-[state=active]:text-white"
            >
              Lists ({lists.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity">
            {logs.length > 0 ? (
              <div className="space-y-4">
                {logs.map((log) => {
                  const albumCover = log.album?.cover_xl || log.album?.cover_big || log.album?.cover_medium || log.album?.cover_url || log.album?.cover_small || "";
                  const albumLink = log.album?.album_id ? `/album/${log.album.album_id}` : "#";
                  
                  return (
                    <Link
                      key={log.log_id}
                      to={albumLink}
                      className="flex gap-4 glass-card rounded-xl p-4 hover:border-purple-500/30 transition-colors"
                      data-testid={`activity-${log.log_id}`}
                    >
                      <img
                        src={albumCover}
                        alt={log.album?.title || "Album"}
                        className="w-16 h-16 rounded-lg object-cover bg-white/5"
                        onError={(e) => { e.target.src = "https://placehold.co/64x64/1a1a2e/9333ea?text=?"; }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate text-white">{log.album?.title || "Unknown Album"}</p>
                        <p className="text-sm text-white/60 truncate">
                          {log.album?.artist || "Unknown Artist"}
                        </p>
                        {log.rating && (
                          <div className="mt-1">
                            <StarRating rating={log.rating} readonly size="small" />
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-white/50">
                        {log.listened_at &&
                          formatDistanceToNow(new Date(log.listened_at), { addSuffix: true })}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                <Disc3 className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">No activity yet</h3>
                <p className="text-white/50">
                  {isOwnProfile
                    ? "Start logging albums to build your profile!"
                    : "This user hasn't logged any albums yet."}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="lists">
            {lists.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {lists.map((list) => (
                  <Link
                    key={list.list_id}
                    to={`/list/${list.list_id}`}
                    className="glass-card rounded-xl p-4 hover:border-purple-500/30 transition-colors"
                    data-testid={`list-${list.list_id}`}
                  >
                    <div className="flex gap-2 mb-3">
                      {list.albums?.slice(0, 4).map((album, i) => {
                        const cover = album.cover_xl || album.cover_big || album.cover_medium || album.cover_url || album.cover_small || "";
                        return (
                          <img
                            key={album.album_id}
                            src={cover}
                            alt={album.title}
                            className="w-12 h-12 rounded object-cover bg-white/5"
                            style={{ marginLeft: i > 0 ? "-8px" : 0 }}
                            onError={(e) => { e.target.src = "https://placehold.co/48x48/1a1a2e/9333ea?text=?"; }}
                          />
                        );
                      })}
                      {(!list.albums || list.albums.length === 0) && (
                        <div className="w-12 h-12 rounded bg-purple-950/50 flex items-center justify-center">
                          <LayoutList className="w-6 h-6 text-white/30" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-semibold truncate text-white">{list.name}</h3>
                    <p className="text-sm text-white/60 truncate">
                      {list.album_ids?.length || 0} albums
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                <LayoutList className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">No lists yet</h3>
                <p className="text-white/50">
                  {isOwnProfile
                    ? "Create your first list to curate your favorites!"
                    : "This user hasn't created any lists yet."}
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
